import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import type { PostgrestError } from "@supabase/supabase-js";
import type { ServiceItem } from "../mocks/services";

// mutation の戻り値。成功時 error: null、失敗時に Postgrest エラーを返し UI 側で分岐する。
type MutationResult = { error: PostgrestError | null };

// DB行 → ServiceItem 変換
// image_url は Supabase Storage('service-images') の公開 URL を保存する方針
// （ServiceFormModal がアップロード→公開 URL を imageUrl に入れて渡す）。
// 過去に保存された base64 data URL が残っていても、そのまま <img src> に渡せば表示は壊れない
// （data: でも http(s): でも有効）。移行はフォーム再保存時に Storage URL へ自然に置き換わる。
function rowToItem(row: Record<string, unknown>): ServiceItem {
  return {
    id: row.id as string,
    name: row.name as string,
    description: row.description as string,
    detail: row.detail as string,
    link: row.link as string | undefined,
    imageUrl: row.image_url as string,
  };
}

export function useServices() {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);

  // 一覧取得
  const fetchServices = async () => {
    const { data, error } = await supabase
      .from("services")
      .select("*")
      .order("sort_order", { ascending: true });
    if (!error && data) {
      setServices(data.map(rowToItem));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const addService = async (item: Omit<ServiceItem, "id">): Promise<MutationResult> => {
    // id は DB の gen_random_uuid() デフォルトに任せる（挿入時に指定しない）。
    // image_url には Storage の公開 URL が入る（base64 直保存は廃止。ServiceFormModal 参照）。
    const { error } = await supabase.from("services").insert({
      name: item.name,
      description: item.description,
      detail: item.detail,
      link: item.link ?? null,
      image_url: item.imageUrl,
      sort_order: services.length,
    });
    if (!error) await fetchServices();
    return { error };
  };

  const updateService = async (
    id: string,
    item: Omit<ServiceItem, "id">
  ): Promise<MutationResult> => {
    // image_url には Storage の公開 URL が入る（base64 直保存は廃止。ServiceFormModal 参照）。
    const { error } = await supabase
      .from("services")
      .update({
        name: item.name,
        description: item.description,
        detail: item.detail,
        link: item.link ?? null,
        image_url: item.imageUrl,
      })
      .eq("id", id);
    if (!error) await fetchServices();
    return { error };
  };

  const deleteService = async (id: string): Promise<MutationResult> => {
    const { error } = await supabase.from("services").delete().eq("id", id);
    if (!error) await fetchServices();
    return { error };
  };

  const reorder = async (from: number, to: number): Promise<MutationResult> => {
    const next = [...services];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // sort_order を一括更新。1件でも失敗したら最初のエラーを返し、サーバ状態で再同期する。
    const results = await Promise.all(
      next.map((item, index) =>
        supabase.from("services").update({ sort_order: index }).eq("id", item.id)
      )
    );
    const firstError = results.find((r) => r.error)?.error ?? null;
    if (firstError) {
      await fetchServices();
      return { error: firstError };
    }
    setServices(next);
    return { error: null };
  };

  return { services, loading, addService, updateService, deleteService, reorder };
}
